import { memo } from 'react'
import { Handle, Position } from '@xyflow/react'
import type { NodeProps, Node } from '@xyflow/react'
import { ArrowDownToLine, ArrowUpFromLine, Package, Link2 } from 'lucide-react'
import type { ProjectExchange } from '@/lib/types/project-graph'

// Define the node data type
type ExchangeNodeData = {
  exchange: ProjectExchange
  exchangeType: 'technosphere' | 'biosphere' | 'production' | 'cross-project'
  selected?: boolean
  isNew?: boolean
  // FU-scaled amount for graph display. When set, overrides exchange.amount on
  // the node card. Activity editor and other recipe-view callers must not pass
  // this, so they keep showing per-unit recipe values.
  displayAmount?: number
}

// Define the full node type
export type ExchangeNodeType = Node<ExchangeNodeData, 'exchange'>

const typeConfig = {
  technosphere: {
    bg: 'bg-blue-500/90',
    border: 'border-blue-400',
    icon: ArrowDownToLine,
    label: 'Input',
    textColor: 'text-blue-200',
  },
  biosphere: {
    bg: 'bg-green-500/90',
    border: 'border-green-400',
    icon: ArrowUpFromLine,
    label: 'Emission',
    textColor: 'text-green-200',
  },
  production: {
    bg: 'bg-amber-500/90',
    border: 'border-amber-400',
    icon: Package,
    label: 'Output',
    textColor: 'text-amber-200',
  },
  'cross-project': {
    bg: 'bg-pink-500/90',
    border: 'border-pink-400 border-dashed',
    icon: Link2,
    label: 'Cross-Project',
    textColor: 'text-pink-200',
  },
}

function ExchangeNodeComponent(props: NodeProps<ExchangeNodeType>) {
  const { exchange, exchangeType, selected, isNew, displayAmount } = props.data
  const renderedAmount = displayAmount ?? exchange.amount
  const config = typeConfig[exchangeType]
  const Icon = config.icon

  // Format amount with appropriate precision
  const formatAmount = (amount: number): string => {
    if (Math.abs(amount) >= 1000) {
      return amount.toLocaleString(undefined, { maximumFractionDigits: 0 })
    }
    if (Math.abs(amount) >= 1) {
      return amount.toLocaleString(undefined, { maximumFractionDigits: 2 })
    }
    if (Math.abs(amount) >= 0.001) {
      return amount.toLocaleString(undefined, { maximumFractionDigits: 4 })
    }
    return amount.toExponential(2)
  }

  return (
    <div
      className={`${config.bg} border rounded-md shadow-md min-w-[180px] max-w-[220px] transition-all duration-150 ${
        selected
          ? 'border-white ring-2 ring-white/50 ring-offset-2 ring-offset-transparent shadow-xl scale-[1.02]'
          : config.border
      } ${isNew ? 'node-highlight-new' : ''}`}
    >
      {/* Handles */}
      <Handle type="target" position={Position.Top} id="top" className="!bg-white/50" />
      <Handle type="target" position={Position.Left} id="left" className="!bg-white/50" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="!bg-white/50" />
      <Handle type="source" position={Position.Right} id="right" className="!bg-white/50" />

      <div className="px-2.5 py-1.5">
        {/* Type badge */}
        <div className="flex items-center gap-1.5 mb-1">
          <Icon className={`w-3 h-3 ${config.textColor}`} />
          <span className={`text-[10px] font-medium ${config.textColor} uppercase tracking-wide`}>
            {config.label}
          </span>
        </div>

        {/* Name */}
        <p className="text-xs font-medium text-white leading-tight line-clamp-2">{exchange.name}</p>

        {/* Amount and unit */}
        <div className="mt-1.5 flex items-baseline gap-1">
          <span
            className={`text-sm font-bold ${renderedAmount < 0 ? 'text-red-300' : 'text-white'}`}
          >
            {formatAmount(renderedAmount)}
          </span>
          <span className={`text-[10px] ${config.textColor}`}>{exchange.unit}</span>
        </div>
      </div>
    </div>
  )
}

export const ExchangeNode = memo(ExchangeNodeComponent)
